/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, Fragment } from "react"
import { useSelector } from "react-redux"
import classnames from "classnames"
import { AppState } from "store/store.type"
import { Image } from "components/Image"
import { numberWithCommas } from "utils/misc"
import { minorUnitToCurrency } from "utils/currency"
import { isItemAvailable } from "utils/item_helper"
import { Item } from "types/models/menu"
import { Row } from "@duxton-web/ui"
import {
  AddGroupFilled,
  ArrowLeftUniversal,
  HeartFilled,
} from "@duxton-web/icons"
import { PhoneActionButton } from "../PhoneActionButton"

const categoryRowStyle = css`
  font-size: 22px;
  font-weight: 500;
  color: #383e43;
`

const itemRowStyle = css`
  display: flex;
  flex-direction: row;
  border-bottom: 1px solid #e8e8e8;
  padding: 14px 0;

  .price {
    font-size: 13px;
    font-weight: 500;
  }

  .unavailable {
    color: rgba(0, 0, 0, 0.45);
  }

  .description {
    font-size: 11px;
    color: #697078;
  }
`

export const MenuPreviewItemRow = ({
  item,
  currency,
}: {
  item: Item
  currency?: string
}) => {
  return (
    <div key={item.itemID} css={itemRowStyle} data-testid="item-container">
      <div
        css={css`
          width: 200px;
          margin-right: 5px;
        `}
      >
        <div>
          <span
            className={classnames(!isItemAvailable(item) && "unavailable")}
            css={css`
              font-size: 13px;
            `}
            data-testid="item-name"
          >
            {item.itemName}
          </span>
        </div>
        <div>
          <span
            className={classnames(
              "price",
              !isItemAvailable(item) && "unavailable",
            )}
          >
            {numberWithCommas(minorUnitToCurrency(item?.priceInMin, currency))}
          </span>
        </div>
        <div>
          <p
            className={classnames(
              "description",
              !isItemAvailable(item) && "unavailable",
            )}
          >
            {item.description}
          </p>
        </div>
      </div>
      {item.imageURL && (
        <Image
          width={90}
          height={90}
          src={item.imageURL}
          data-testid="item-image"
          opacity={isItemAvailable(item) ? 1 : 0.5}
        />
      )}
    </div>
  )
}

export const MenuPreview: FC = () => {
  const {
    overview: { categories, departments },
    outlet,
    outletV2,
    isMartDepartmentStructure,
    enableTroyNewMerchantEndpoint,
  } = useSelector((state: AppState) => state.menu)
  const currencyCode = enableTroyNewMerchantEndpoint
    ? outletV2?.currency?.code
    : outlet?.currency
  const backgroundImg = enableTroyNewMerchantEndpoint
    ? outletV2?.backgroundImg
    : outlet?.backgroundImg
  const outletName = enableTroyNewMerchantEndpoint
    ? outletV2?.name
    : outlet?.merchantName

  return (
    <div css={css``}>
      <div
        css={css`
          position: absolute;
          top: 0;
          padding: 30px 9px;
          width: 100%;
          background: url(${backgroundImg});
          background-repeat: no-repeat;
          background-position: top left;
          background-size: contain;
          height: 280px;
        `}
      >
        <Row justify="space-between">
          <PhoneActionButton
            icon={<ArrowLeftUniversal style={{ color: "#ffffff" }} />}
          />
          <PhoneActionButton
            icon={<HeartFilled style={{ color: "#d42e1c" }} />}
          />
        </Row>
        <Row
          css={css`
            margin-top: 7px;
          `}
          justify="end"
        >
          <PhoneActionButton
            icon={<AddGroupFilled style={{ color: "#ffffff" }} />}
          />
        </Row>
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
        `}
      >
        <div
          css={css`
            height: 155px;
            width: 100%;
          `}
        />
        <div
          css={css`
            z-index: 1;
            padding: 0 16px;
            width: 100%;
            background: rgb(255, 255, 255);
            background: linear-gradient(
              0deg,
              rgba(255, 255, 255, 1) 90%,
              rgba(0, 0, 0, 0) 100%
            );
          `}
        >
          <div
            css={css`
              height: 160px;
              width: 100%;
              position: relative;
            `}
          >
            <div
              css={css`
                width: 100%;
                background: #ffffff;
                box-shadow: 0px 7.32808px 14.6562px rgba(26, 26, 26, 0.1);
                border-radius: 10.9921px;

                height: 114.5px;
                padding: 14.66px;
              `}
            >
              <span
                css={css`
                  font-size: 22px;
                  font-weight: 500;
                `}
              >
                {outletName}
              </span>
            </div>
          </div>
          {/* Food Structure */}
          {!isMartDepartmentStructure && (
            <Fragment>
              {categories.map(category => (
                <div data-testid="category-container" key={category.categoryID}>
                  <div>
                    <span css={categoryRowStyle} data-testid="category-name">
                      {category.categoryName}
                    </span>
                  </div>
                  {category.items?.map(item => (
                    <MenuPreviewItemRow item={item} currency={currencyCode} />
                  ))}
                </div>
              ))}
            </Fragment>
          )}
          {/* Mart-Department Structure */}
          {isMartDepartmentStructure && (
            <Fragment>
              {departments.map(department => (
                <div
                  data-testid="category-container"
                  key={department.itemClassID}
                >
                  <div>
                    <span css={categoryRowStyle} data-testid="category-name">
                      {department.itemClassName}
                    </span>
                  </div>
                  {department.subDepartments.map(subDepartment => (
                    <div
                      data-testid="category-container"
                      key={subDepartment.itemClassID}
                    >
                      {subDepartment.items?.map(item => (
                        <MenuPreviewItemRow
                          item={item}
                          currency={currencyCode}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </Fragment>
          )}
        </div>
      </div>
    </div>
  )
}
